/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import Select from "react-select";
import { BiologicalSexOptions } from "../../models/biological-sex";
import FirestoreCondition from "../../models/interfaces/firestore-condition";
import { Group } from "../../models/interfaces/group";
import { ListOptions } from "../../models/interfaces/list-options";
import { Tag } from "../../models/interfaces/tag";
import { User } from "../../models/interfaces/user";
import { UserTag } from "../../models/interfaces/user-tag";
import UserRoles from "../../models/user-roles";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import FunctionService from "../../utilities/services/function-service";
import GroupService from "../../utilities/services/group-service";
import OrganizationService from "../../utilities/services/organization-service";
import OrganizationUtil from "../../utilities/organization-util";
import TagService from "../../utilities/services/tag-service";
import UserService from "../../utilities/services/user-service";
import UserUtil from "../../utilities/user-util";
import { Utils } from "../../utilities/utils";
import { Button } from "../button/button";
import { createLogger } from "../../utilities/logger";
import { Loader } from "../loader/loader";
import { Modal } from "../modal/modal";
import * as TagComponent from "../tag/tag";
import { Organization } from "../../models/interfaces/organization";
import ChangeAccountHolderModal from "../user-list/change-account-holder-modal";
import RemoveAccountHolderModal from "../user-list/remove-account-holder-modal";
import { ReactComponent as ProfileIcon } from "../../assets/icons/nav/icon_profile.svg";
import { UserStatus } from "../../models/enumerations/user-status";
import { SymmioAccessType } from "../../models/enumerations/symmio-access-type";
import { enqueueSnackbar } from "notistack";
import { SymmioLicenseCheckbox } from "../symmio-license-checkbox";
import DateInputOption from "../account-information-date-input-option/account-information-date-input-option";
import dayjs from "dayjs";
import { UserStatusBadge } from "../user-status-badge/user-status-badge";
import { TextTypeInput, ToggleButton } from "../forms";
import { useForm } from "react-hook-form";
import { MskScore } from "../../models/interfaces/msk-score";
import { useTranslation } from 'react-i18next';
import { LanguageDateFormat } from "../../models/language-date-format";
import ReportDataUtil from "../../utilities/report-data-util";
import { PlanType } from "../../models/enumerations/plan-type";
import OrganizationSubscriptionService from "../../utilities/services/organization-subscription-service";
import { AddOnId } from "../../models/enumerations/add-on-id";
import BillingService from "../../utilities/services/billing-service";

const log = createLogger("UserProfileHeader");

const COMPONENT_CLASS = "c-user-profile-header";

/**
 * Active users must have a valid birthdate to save. Pending / inactive users
 * (e.g. invites that have not completed setup) may be saved without one.
 */
const isBirthdateAcceptable = (u: User): boolean =>
    u.status !== UserStatus.Active || (!!u.birthdate && dayjs(u.birthdate).isValid());

// -----------------------------------------------------------------------------------------
// #region Intefaces
// -----------------------------------------------------------------------------------------

interface UserProfileHeaderProps {
    user: User,
    greenToast: Function,
    products: any[],
    latestMskData?: MskScore,
}

// #endregion Intefaces

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const UserProfileHeader: React.FC<UserProfileHeaderProps> = (props: UserProfileHeaderProps) => {
    const { t, i18n } = useTranslation();
    const { user, greenToast, products, latestMskData } = props;
    const [editUser, setEditUser] = useState<User>();
    const [isEditing, setIsEditing] = useState(false);
    const [isLoadingGroups, setIsLoadingGroups] = useState(false);
    const [isLoadingTags, setIsLoadingTags] = useState(false);
    const [tagsLoaded, setTagsLoaded] = useState<boolean>(false);
    const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [organization, setOrganization] = useState<Organization>();
    const [subOrganizationOptions, setSubOrganizationOptions] = useState<Organization[]>([]);
    const [groups, setGroups] = useState<Group[]>([]);
    const [tags, setTags] = useState<Tag[]>([]);
    const [userOrgName, setUserOrgName] = useState<string>(t('userProfile.header.loadingOrg'));
    const [isUserLimitReached, setIsUserLimitReached] = useState<boolean>(false);
    const [isBirthdateValid, setIsBirthdateValid] = useState<boolean>(isBirthdateAcceptable(user));
    const [isSymmioAccess, setIsSymmioAccess] = useState<boolean>(false);
    const hadSymmioAccessBefore = user.symmioAccess === SymmioAccessType.AppLicense;
    const wasDeactivatedBefore = user.status === UserStatus.Disabled;
    const [isUserDeactivated, setIsUserDeactivated] = useState<boolean>(wasDeactivatedBefore);
    const originalStatus = user.status;
    const [canEdit, setCanEdit] = useState<boolean>(false);
    const [canEditStatus, setCanEditStatus] = useState<boolean>(false);

    // account holder state variables
    const [editUserOrg, setEditUserOrg] = useState<Organization>();
    const [currentAccountHolder, setCurrentAccountHolder] = useState<User>();
    const [changeAccountHolderModalOpen, setChangeAccountHolderModalOpen] = useState<boolean>(false);
    const [overwritingAccountHolder, setOverwritingAccountHolder] = useState<boolean>(false);
    const [removingAccountHolder, setRemovingAccountHolder] = useState<boolean>(false);
    const [removeAccountHolderModalOpen, setRemoveAccountHolderModalOpen] = useState<boolean>(false);
    const [roleDropdownOptions, setRoleDropdownOptions] = useState<ListOptions[]>();
    const [editUserCurrentOrg, setEditUserCurrentOrg] = useState<Organization | undefined>();
    const [noHolderOrgSwitch, setNoHolderOrgSwitch] = useState<boolean>(false);
    const [isAccountHolder, setIsAccountHolder] = useState<boolean>(false);
    const [overLicenseLimit, setOverLicenseLimit] = useState<boolean>(false);
    const { state, setState } = useAuthState();

    const isPortalUserManager = UserUtil.isManager(state.user);
    const isSuperAdmin = UserUtil.isSuperAdmin(state.claims);
    const isUserAccountHolder = UserUtil.isAccountHolder(state.user);
    const isUserAdmin = UserUtil.isAdmin(state.user);

    // Super admins can move a user to any organization. Account holders can
    // only move users within their own organization tree (head plus sub-orgs).
    const accountHolderHeadId = state.user?.organizationId ?? state.organization?.id;
    const organizationOptions = isSuperAdmin
        ? organizations
        : organizations.filter((o) => o.id === accountHolderHeadId || o.parentOrganizationId === accountHolderHeadId);
    const canChangeOrganization = isSuperAdmin || (isUserAccountHolder && organizationOptions.length > 1);

    const methods = useForm<any>();
    const {
        register,
    } = methods;

    useEffect(() => {
        const getOrg = async () => {
            if (editUser && editUser.organizationId) {
                const org = await OrganizationService.get(editUser.organizationId);
                if (org) {
                    setIsUserLimitReached(await OrganizationService.isUserLimitReached(org));
                    setOrganization(org);
                }
            };
        }
        getOrg();
    }, [editUser?.organizationId, isEditing]);

    useEffect(() => {
        if (editUser && editUser.roles && editUser.roles[0]) {
            if (editUser.roles[0] === UserRoles.ACCOUNT_HOLDER_ID) {
                setIsAccountHolder(true);
            }
        } else if (user && user.roles && user.roles[0]) {
            if (user.roles[0] === UserRoles.ACCOUNT_HOLDER_ID) {
                setIsAccountHolder(true);
            }
        } else {
            setIsAccountHolder(false);
        }
    }, [editUser?.roles, editUser]);

    useEffect(() => {
        const getOrg = async () => {
            if (editUser && editUser.organizationId) {
                // don't bother getting an org we already have
                if (editUserCurrentOrg && editUser.organizationId === editUserCurrentOrg.id) return;
                const org = await OrganizationService.get(editUser.organizationId);
                if (org) {
                    setEditUserCurrentOrg(org);
                }
                return;
            }

            if (user && user.organizationId) {
                // don't bother getting an org we already have
                if (editUserCurrentOrg && user.organizationId === editUserCurrentOrg.id) return;
                const org = await OrganizationService.get(user.organizationId);
                if (org) {
                    setEditUserCurrentOrg(org);
                }
                return;
            }
        };
        if (isAccountHolder) {
            getOrg();
        }
    }, [editUser?.organizationId, isAccountHolder]);

    useEffect(() => {
        getRolesDropdownOptions()
            .then((response) => {
                // we need to check if the role selected by the user is still
                // present in the new role options
                if (editUser && editUser.roles && editUser.roles[0]) {
                    const editUserRole = editUser.roles[0];
                    const userRoleInOptions = !!response.find((r: any) => r.value === editUserRole);
                    // if the role selected by the user is not in the new set of
                    // role options, we need to clear the field
                    if (!userRoleInOptions && canEditRoles()) {
                        setEditUser(Object.assign({}, editUser, { roles: [] }));
                    }
                }
                setRoleDropdownOptions(response);
            })
    }, [user, editUser?.organizationId, editUserCurrentOrg]);

    useEffect(() => {
        if (editUser?.id !== user.id) {
            let clonedUser = Object.assign({}, user);
            setEditUser(clonedUser);
            setIsSymmioAccess(clonedUser?.symmioAccess === SymmioAccessType.AppLicense);
            setIsUserDeactivated(clonedUser.status === UserStatus.Disabled);
            setIsBirthdateValid(isBirthdateAcceptable(clonedUser));
        }
    }, [editUser, user]);

    useEffect(() => {
        // Show the viewed user's organization when it differs from the
        // currently selected one (e.g. a super admin, or an account holder /
        // admin browsing sub-organizations via search all organizations).
        let cancelled = false;

        const loadOrg = async () => {
            if (state.user == null || user?.organizationId == null) {
                return;
            }

            try {
                const canViewOrg = isSuperAdmin
                    || (await UserUtil.getAccessibleOrganizationIds(state.user)).includes(user.organizationId);

                if (!canViewOrg) {
                    if (!cancelled) {
                        setUserOrgName(t('userProfile.header.orgNotFound'));
                    }
                    return;
                }

                const org = await OrganizationService.get(user.organizationId);

                if (!cancelled) {
                    setUserOrgName(org?.name ?? t('userProfile.header.orgNotFound'));
                }
            } catch (e) {
                log.error("Failed to load user's organization", {
                    exception: e,
                    organizationId: user.organizationId,
                });
                if (!cancelled) {
                    setUserOrgName(t('userProfile.header.orgNotFound'));
                }
            }
        };

        if (
            user?.organizationId != null
            && state.organization?.id != null
            && state.organization.id !== user.organizationId
        ) {
            setUserOrgName(t('userProfile.header.loadingOrg'));
            loadOrg();
        }

        return () => {
            cancelled = true;
        };
    }, [user?.organizationId, state.organization?.id, state.user, isSuperAdmin]);

    useEffect(() => {
        if (isEditing && editUser && !tagsLoaded) {
            setIsLoadingTags(true);

            TagService.getBy([{
                field: "organizationId",
                operator: "==",
                value: user.organizationId
            }], [{
                field: "name",
                direction: "asc",
            }]).then((tags: Tag[]) => {
                setTags(tags);
                setIsLoadingTags(false);
                setTagsLoaded(true);
            });

            OrganizationService.getAll().then((organizations: Organization[]) => {
                if (organizations) {
                    organizations = organizations.sort((a, b) => !!a.name ? a.name.localeCompare(!!b.name ? b.name : "undefined") : "undefined".localeCompare(!!b.name ? b.name : "undefined"));
                }

                setOrganizations(organizations);
                setIsLoadingOrganizations(false);
            });
        }
    }, [editUser, isEditing, user.organizationId]);

    // Load the sub-organizations the editor is allowed to grant to an admin
    // (same source/logic as the add-admin flow). Only account holders and
    // super admins can manage an admin's sub-organization access, and only
    // when the admin's (target) organization is a head organization.
    useEffect(() => {
        if (
            !isEditing
            || !editUser
            || !UserUtil.isAdmin(editUser)
            || OrganizationUtil.isSubOrganization(organization)
            || (!isUserAccountHolder && !isSuperAdmin)
        ) {
            setSubOrganizationOptions([]);
            return;
        }

        // Ignore stale responses if the target organization changes again
        // before the options have loaded.
        let cancelled = false;

        UserUtil.getAdminSubOrganizationAccessOptions(state.user, organization).then((orgs) => {
            if (!cancelled) {
                setSubOrganizationOptions(orgs);
            }
        }).catch((e: any) => {
            log.error("Failed to load sub-organization access options", {
                exception: e,
                organizationId: organization?.id,
            });
        });

        return () => {
            cancelled = true;
        };
    }, [isEditing, editUser?.roles, editUser?.organizationId, organization?.id, isUserAccountHolder, isSuperAdmin]);

    useEffect(() => {
        if (state.user == null || user?.organizationId == null) {
            return;
        }

        setIsLoadingGroups(true);

        const loadGroups = async () => {
            let conditions: FirestoreCondition[] = [
                {
                    field: "organizationId",
                    operator: "==",
                    value: user.organizationId
                }
            ];

            if (UserUtil.isManager(state.user)) {
                const groupIds = await UserUtil.getHierarchicalGroupIds(state.user?.managedGroupIds);
                conditions.push({
                    field: "id",
                    operator: "in",
                    value: groupIds,
                });
            }

            GroupService.getBy(
                conditions,
                [{
                    field: "name",
                    direction: "asc",
                }]
            ).then((groups: Group[]) => {
                setGroups(groups);
                setIsLoadingGroups(false);
            });
        }

        loadGroups();
    }, [user.organizationId, state.user]);

    useEffect(() => {
        if (!isEditing) {
            let clonedUser = Object.assign({}, user);
            setEditUser(clonedUser);
            setIsSymmioAccess(clonedUser?.symmioAccess === SymmioAccessType.AppLicense);
            setIsUserDeactivated(clonedUser.status === UserStatus.Disabled);
        }
    }, [isEditing, user]);

    const saveUser = async () => {
        if (!editUser) {
            return;
        }

        if (isSymmioAccess) {
            setEditUser(Object.assign({}, editUser, { symmioAccess: SymmioAccessType.AppLicense }));
            editUser.symmioAccess = SymmioAccessType.AppLicense;
        }
        else {
            setEditUser(Object.assign({}, editUser, { symmioAccess: SymmioAccessType.WebAccess }));
            editUser.symmioAccess = SymmioAccessType.WebAccess;
        }

        // If deactivated - remove license
        if (editUser.status === UserStatus.Disabled) {
            setEditUser(Object.assign({}, editUser, { symmioAccess: SymmioAccessType.WebAccess }));
            editUser.symmioAccess = SymmioAccessType.WebAccess;
            setIsSymmioAccess(false);
        }

        try {
            editUser.tagIds = editUser.tags?.map(t => t.id) || [];

            // Keep accessible sub-organizations consistent with the role and
            // with the sub-organizations the editor is allowed to grant.
            if (editUser.roles?.[0] === UserRoles.ADMIN_ID) {
                if (!OrganizationUtil.isSubOrganization(organization) && subOrganizationOptions.length > 0) {
                    const allowedIds = subOrganizationOptions.map((o) => o.id).filter((id): id is string => id != null);
                    editUser.accessibleSubOrganizationIds = (editUser.accessibleSubOrganizationIds ?? [])
                        .filter((id) => allowedIds.indexOf(id) >= 0);
                }
            } else {
                editUser.accessibleSubOrganizationIds = [];
            }

            // If user was given an app license, we must update addOns tracker if needed
            if (user.symmioAccess !== SymmioAccessType.AppLicense && editUser.symmioAccess === SymmioAccessType.AppLicense && (organization?.plan?.value === PlanType.Business || organization?.plan?.value === PlanType.Core || organization?.plan?.value === PlanType.Pro)) {
                const orgSub = await OrganizationSubscriptionService.getByOrganizationId(organization.id!);
                if (orgSub && orgSub.length > 0 && orgSub[0].stripeAddOnSubscription) {
                    const userAddOnPriceId = products
                        .find((item) => item.product.id === AddOnId.User)
                        ?.prices.find((item: any) => item.data.active === true)?.id;
                    const licenseAddOnPriceId = products
                        .find((item) => item.product.id === AddOnId.License)
                        ?.prices.find((item: any) => item.data.active === true)?.id;

                    if (userAddOnPriceId && licenseAddOnPriceId) {
                        await BillingService.checkAndUpdateHiddenLimit({
                            stripeAddOnSubscriptionId: orgSub[0].stripeAddOnSubscription,
                            userAddOnPriceId,
                            licenseAddOnPriceId,
                            currentLicenseCount: organization?.mySymmioLicenseCount ?? 0,
                        });
                    }
                }
            }

            // Persist an empty birthdate as null so downstream `!= null`
            // checks skip it consistently (empty strings break date math).
            if (!editUser.birthdate) {
                editUser.birthdate = null;
            }

            const isOrganizationMove = editUser.organizationId !== user.organizationId;

            if (isOrganizationMove) {
                // The move is handled server-side so the user's organization,
                // group/tag cleanup and user-linked documents are updated
                // together.
                await FunctionService.swapUserOrganization(editUser.id!, editUser.organizationId!, {
                    firstName: editUser.firstName,
                    lastName: editUser.lastName,
                    biologicalSex: editUser.biologicalSex,
                    birthdate: editUser.birthdate,
                    roles: editUser.roles,
                    status: editUser.status,
                    symmioAccess: editUser.symmioAccess,
                    groupId: editUser.groupId ?? null,
                    tags: editUser.tags,
                    managedGroupIds: editUser.managedGroupIds,
                    accessibleSubOrganizationIds: editUser.accessibleSubOrganizationIds,
                });
            } else {
                await UserService.update(editUser, state.user);
                await updateTagUserCount(user, editUser, state.user);
            }

            if (editUser.authenticationId && editUser.roles && editUser.roles.length > 0) {
                await FunctionService.updateUserRoles(editUser);
            }

            log.info("User profile updated by admin", {
                subjectUserId: editUser.id,
                organizationId: editUser.organizationId,
                roles: editUser.roles,
                symmioAccess: editUser.symmioAccess,
                status: editUser.status,
                organizationMove: isOrganizationMove,
                sourceOrganizationId: user.organizationId,
            });

        } catch (err: any) {
            // TODO : Global exception handling
            // https://app.clickup.com/t/2219993/FMS-1236
            log.error("Failed to save user profile", {
                exception: err,
                userProfileId: editUser?.id,
            });

            const errorMessage = String(err?.message ?? "");

            if (errorMessage.indexOf("USER_LIMIT_REACHED") >= 0) {
                enqueueSnackbar(t('userProfile.header.userLimit'));
            } else if (errorMessage.indexOf("LICENSE_LIMIT_REACHED") >= 0) {
                enqueueSnackbar(t('userProfile.header.limitReached'));
            } else {
                enqueueSnackbar(t('userProfile.header.errorSaving'));
            }

            // Keep the modal open so the edit can be corrected and retried.
            setIsSaving(false);
            return;
        }

        // if the edit user is set to be saved as an account holder, we need to
        // update their organization and set their id as the accountHolderId
        if (editUser.roles && editUser.roles[0] === UserRoles.ACCOUNT_HOLDER_ID && editUser.organizationId) {
            const org = await OrganizationService.get(editUser.organizationId);
            if (org) {
                org.accountHolderId = editUser.id;
                await OrganizationService.update(org, state.user);
            }
            const stateOrg = state.organization;
            stateOrg.accountHolderId = editUser.id;
            setState((state) => ({
                ...state, ...{ organization: stateOrg }
            }));
        }

        setOverLicenseLimit(false);
        setIsEditing(false);
        setIsSaving(false);
        setOverwritingAccountHolder(false);
        setRemovingAccountHolder(false);
        const info = { orgName: organizations.find(({ id }) => id === editUser?.organizationId)?.name, accountOwnerEmail: state.user?.email };
        if (isSymmioAccess !== hadSymmioAccessBefore && isSymmioAccess) {
            FunctionService.sendMySymmioAccessAssigned(editUser, info);
        } else if (isSymmioAccess !== hadSymmioAccessBefore && !isSymmioAccess) {
            FunctionService.sendMySymmioAccessRemoved(editUser, info);
        }
        greenToast();
    };

    const handleSaveUserClick = async () => {
        if (!editUser) {
            return;
        }
        setIsSaving(true);

        // if the user has the account holder role, check to see if there is
        // already an account holder for their organization.
        if (editUser.roles && editUser?.roles.length > 0 && editUser.organizationId) {
            if (editUser?.roles.indexOf(UserRoles.ACCOUNT_HOLDER_ID) !== -1) {
                const userOrg = await OrganizationService.get(editUser.organizationId);

                const alreadyHolder = userOrg?.accountHolderId === editUser.id;

                // if the organization has an account holder, open the change
                // account holder modal
                if (!alreadyHolder && userOrg?.accountHolderId) {
                    const currentHolder = await UserService.get(userOrg?.accountHolderId);
                    setEditUserOrg(userOrg);
                    setCurrentAccountHolder(currentHolder!);
                    setChangeAccountHolderModalOpen(true);
                    return;
                }
            }
        }

        if (removingAccountHolder && user.organizationId) {
            const userOrg = await OrganizationService.get(user.organizationId);
            if (userOrg) {
                setEditUserOrg(userOrg);
            }
            setRemoveAccountHolderModalOpen(true);
            return;
        }


        await saveUser();
    }

    const isManagingGroups = (roles?: string[]): boolean => {
        if (roles == null || roles.length === 0) {
            return false;
        }
        return (roles.indexOf(UserRoles.GROUP_MANAGER_ID) >= 0 || roles.indexOf(UserRoles.TEAM_MEMBER_ID) >= 0)
            && roles.indexOf(UserRoles.ADMIN_ID) === -1;
    }

    const handleOrganizationChange = (newValue: any) => {
        // Remove the group, tag, managed-group and sub-organization access
        // values from the editUser and assign the new organization.
        if (editUser) {
            // Note: Had to change the interface to accept null values. Passing undefined to firebase wasn't persisting the new data change.
            editUser.groupId = null;
            editUser.groupName = null;
            // Clear unconditionally: legacy user docs may have tags without
            // tagIds, which would otherwise leave old-organization tags selected.
            editUser.tags = [];
            editUser.tagIds = [];
            editUser.managedGroupIds = [];
            editUser.accessibleSubOrganizationIds = [];
            editUser.organizationName = newValue.label;
            editUser.organizationId = newValue.value;
            setEditUserCurrentOrg(organizations.find(({ id }) => id === newValue.value));
        }

        // If the organization is changed back to the original one
        // We want to set the editUser's group and tags values to what they currently are
        if (newValue.value === user.organizationId && editUser) {
            editUser.groupId = user.groupId;
            editUser.groupName = user.groupName;
            editUser.tags = user.tags ?? [];
            editUser.tagIds = user.tagIds ?? [];
            editUser.managedGroupIds = user.managedGroupIds;
            editUser.accessibleSubOrganizationIds = user.accessibleSubOrganizationIds;
        }

        // We now need to change the values stored in our tags/groups list
        // We want to grab all tags/groups that have the organizationId of the editUser object
        // Grabbing new Tags
        if (isEditing && editUser) {
            setIsLoadingTags(true);

            TagService.getBy([{
                field: "organizationId",
                operator: "==",
                value: newValue.value
            }], [{
                field: "name",
                direction: "asc",
            }]).then((tags: Tag[]) => {
                setTags(tags);
                setIsLoadingTags(false);
            }).then(() => {
                setIsLoadingGroups(true);

                // Grabbing new Groups
                let conditions: FirestoreCondition[] = [
                    {
                        field: "organizationId",
                        operator: "==",
                        value: newValue.value
                    }
                ];

                // !Not sure if this is needed!
                // if (UserUtil.isManager(state.user)) {
                //     const groupIds = await UserUtil.getHierarchicalGroupIds(state.user?.managedGroupIds);
                //     conditions.push({
                //         field: "id",
                //         operator: "in",
                //         value: groupIds,
                //     });
                // }

                GroupService.getBy(
                    conditions,
                    [{
                        field: "name",
                        direction: "asc",
                    }]
                ).then((groups: Group[]) => {
                    setGroups(groups);
                    setIsLoadingGroups(false);
                });
            });
        }
    };

    const handleSymmioAccessCheckbox = () => {
        setIsSymmioAccess(!isSymmioAccess);
    }

    const statusBadge = (
        <div className="flex items-center">
            <UserStatusBadge status={user.status} />
        </div>
    );

    let birthdate = t('userProfile.header.notProvided');
    if (user.birthdate) {
        birthdate = dayjs(user.birthdate).format(LanguageDateFormat[i18n.language]);
        // const date = user.birthdate.split('-');
        // if (date.length === 3) {
        //     birthdate = `${date[1]}/${date[2]}/${date[0]}`
        // }
    }

    const managedGroupOptions: ListOptions[] = groups.map((g) => ({ label: g.name, value: g.id }));

    const canEditRoles = () => {
        // account holders can only have their role changed by a super admin
        if (UserUtil.isAccountHolder(user) && !isSuperAdmin) {
            return false;
        }
        if (UserUtil.isAccountHolder(user) && isSuperAdmin) {
            return true;
        }
        if (!isPortalUserManager || UserUtil.hasNoRole(user) || UserUtil.isTeamMember(user)) {
            return true;
        }
        return false;
    };

    // Role - Allow to Edit:
    // Account Holders - Everyone
    // Admins - Admins, Group Managers, Team Members
    // Group Managers - Group Managers, Team Members
    useEffect(() => {
        const checkCanEdit = async () => {
            // Account Holders can be edited by Super Admins and Account Holders
            if (UserUtil.isAccountHolder(user) && (isSuperAdmin || isUserAccountHolder)) {
                setCanEdit(true);
            }
            // Admins can be edited by Super Admins, Acc Holders and Admins
            else if (UserUtil.isAdmin(user) && (isSuperAdmin || isUserAccountHolder || isUserAdmin)) {
                setCanEdit(true);
                setCanEditStatus(true);
            }
            // Group Managers, Team Members and Clients can be edited by Super Admins, Acc Holders, Admins and
            // Group Managers if they're in their group
            else if ((UserUtil.isManagerOrTeamMember(user) || UserUtil.hasNoRole(user) || UserUtil.isClient(user)) && (isSuperAdmin || isUserAccountHolder || isUserAdmin)) {
                setCanEdit(true);
                setCanEditStatus(true);
            }
            else if ((UserUtil.isManagerOrTeamMember(user) || UserUtil.hasNoRole(user) || UserUtil.isClient(user)) && isPortalUserManager) {
                const groupIds = await UserUtil.getHierarchicalGroupIds(state.user?.managedGroupIds);

                if (user?.groupId && groupIds.includes(user.groupId)) {
                    setCanEdit(true);
                    setCanEditStatus(true);
                }
            }
            else {
                setCanEdit(false);
                setCanEditStatus(false);
            }

            //Check can edit status for Acc Holders
            if (UserUtil.isAccountHolder(user) && isSuperAdmin) {
                setCanEditStatus(true);
            }
        }

        checkCanEdit();
    }, [isPortalUserManager, isSuperAdmin, isUserAccountHolder, isUserAdmin, state.user?.managedGroupIds, user]);

    const roles = editUser?.roles?.filter(r => r).map(r => UserRoles.ALL_ROLES.find(o => o.value === r));
    const role = roles == null || roles.length === 0 ? [] : roles[0];
    // let symmioAccess = SymmioAccessTypeOptions.find((x) => x.value === editUser?.symmioAccess);

    const getRolesDropdownOptions = async () => {
        if (isSuperAdmin) {
            setNoHolderOrgSwitch(false);
            // we don't want to allow account holder if the edit user is not in
            // their original organization
            if (editUser?.organizationId !== user.organizationId) {
                setNoHolderOrgSwitch(true);
                // we need to make a copy without reference or the disabled
                // option will always be disabled
                const roles = JSON.parse(JSON.stringify(UserRoles.ALL_ROLES));
                const holderIndex = roles.findIndex((x: any) => x.value === UserRoles.ACCOUNT_HOLDER_ID);
                roles[holderIndex].isDisabled = true;
                return roles;
            }

            // we don't want to allow account holders in sub-organizations
            if (editUser?.organizationId) {
                // get the current org selected on the edit screen
                const currentOrg = await OrganizationService.get(editUser?.organizationId);
                // ensure we have an org and it has an account holder
                if (currentOrg && currentOrg.accountHolderId) {
                    // get the account holder for the current org
                    const currentOrgHolder = await UserService.get(currentOrg?.accountHolderId);
                    // if the account holder for the current org is not in that
                    // org, then it is a sub-organization and we don't want to
                    // have the account holder role to be in the dropdown
                    if (currentOrgHolder && currentOrgHolder.organizationId) {
                        if (currentOrgHolder.organizationId !== currentOrg.id) {
                            return UserRoles.ADMIN_ROLES;
                        }
                    }
                }
            }

            // we don't want to allow the user to be an account holder if they
            // aren't in an organization (not sure if this is possible, but to
            // be safe...)
            if (!user?.organizationId) {
                return UserRoles.ADMIN_ROLES;
            }

            // if we get this far, it's okay to include the account holder role
            return UserRoles.ALL_ROLES;
        }
        if (isPortalUserManager) {
            return UserRoles.MANAGER_ROLES;
        }

        // Admins (who are not account holders) cannot make other users admins.
        if (isUserAdmin && !isUserAccountHolder) {
            const roles = JSON.parse(JSON.stringify(UserRoles.ADMIN_ROLES));
            const adminIndex = roles.findIndex((r: any) => r.value === UserRoles.ADMIN_ID);
            if (adminIndex >= 0) {
                roles[adminIndex].isDisabled = true;
            }
            return roles;
        }

        return UserRoles.ADMIN_ROLES
    };

    /**
     * Handles changing of the role dropdown. Mostly logic for how to check for
     * and handle account holders being overwritten or removed.
     *
     * @param newValue The role selected by the user.
     * @returns Nothing
     */
    const handleRoleChange = async (newValue: any) => {
        setOverwritingAccountHolder(false);
        setRemovingAccountHolder(false);

        let newRole = null;
        if (newValue && Object.hasOwn(newValue, 'value')) {
            newRole = newValue.value;
        }

        let newValues = {};

        // if the user is being changed to be an account holder, we need to make
        // sure they stay in their original organization
        if (newRole === UserRoles.ACCOUNT_HOLDER_ID) {
            newValues = { organizationId: user.organizationId };
        }

        // if the user is being changed to be an account holder, we need to get their starting limits
        if (newRole === UserRoles.ACCOUNT_HOLDER_ID && user?.organizationId) {
            const org = await OrganizationService.get(user?.organizationId);
            const alreadyHolder = org?.accountHolderId === user.id;

            if (!alreadyHolder) {
                // if the users organization has an account holder, we need to
                // warn that they are overwriting it
                if (org?.accountHolderId && !alreadyHolder) {
                    const accHolder = await UserService.get(org.accountHolderId);
                    if (accHolder) {
                        newValues = {
                            ...newValues,
                            roles: newValue == null ? [] : [newRole],
                        };
                        setEditUser(Object.assign({}, editUser, newValues));
                        setOverwritingAccountHolder(true);
                        return;
                    }
                    // otherwise just default the limits and counts to the edit user's organization's counts
                }
            } else {
                setOverwritingAccountHolder(false);
            }
        }

        if (user.roles && (user.roles[0] === UserRoles.ACCOUNT_HOLDER_ID)) {
            if (editUser && editUser.roles && (newRole !== UserRoles.ACCOUNT_HOLDER_ID)) {
                setRemovingAccountHolder(true);
            } else {
                setRemovingAccountHolder(false);
            }
        }

        setEditUser(Object.assign({}, editUser, { roles: newValue == null ? [] : [newRole] }));
    };

    const handleAccountHolderModalClose = (save: boolean) => {
        setChangeAccountHolderModalOpen(false);
        setRemoveAccountHolderModalOpen(false);
        if (save) {
            saveUser();
        }
        setIsSaving(false);
        return;
    };

    const handleCloseUserEditModal = () => {
        setOverLicenseLimit(false);
        setRemovingAccountHolder(false);
        setOverwritingAccountHolder(false);
        setIsEditing(false);
    }

    const handleToggleSwitch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;

        setIsUserDeactivated(checked);
        if (!checked && wasDeactivatedBefore) {
            setEditUser(Object.assign({}, editUser, { status: UserStatus.Inactive }));
        }
        else if (checked && !wasDeactivatedBefore) {
            setEditUser(Object.assign({}, editUser, { status: UserStatus.Disabled }));
            setIsSymmioAccess(false);
        }
        else {
            setEditUser(Object.assign({}, editUser, { status: originalStatus }));
        }
    }

    const handleBirthdateChange = (date: string) => {
        setEditUser(Object.assign({}, editUser, { birthdate: dayjs(date).format('YYYY-MM-DD') }));
        setIsBirthdateValid(true);
    }

    const handleBirthdateClear = () => {
        setEditUser(Object.assign({}, editUser, { birthdate: null }));
        setIsBirthdateValid(editUser?.status !== UserStatus.Active);
    }

    const handleUserName = (user: any) => {
        if (!user.lastName && !user.firstName)
            return user.email.split("@")[0];
        else
            return `${user.firstName ?? ''} ${user.lastName ?? ''}`
    }

    return (
        <div className={COMPONENT_CLASS}>
            <Loader isVisible={isSaving} />
            <div className={`${COMPONENT_CLASS}__actions`}>
                <h1><span>{handleUserName(user)}</span></h1>
                <div className="flex flex-row flex-wrap-reverse gap-4 justify-center">
                    <div className="flex items-center">
                        {statusBadge}
                    </div>
                    {latestMskData && latestMskData.movementScore?.percentage != null && latestMskData.lifestyleScore?.percentage != null && (
                        <Button
                            onClick={() => ReportDataUtil.downloadWellness(user, latestMskData)}
                            buttonText={t('buttons.btn_downloadLatestReport')} />
                    )}
                    {canEdit &&
                        <Button
                            onClick={() => setIsEditing(true)}
                            buttonText={t('buttons.btn_edit')} />
                    }
                </div>
            </div>
            <div className={`${COMPONENT_CLASS}__col-container`}>
                <div className={`${COMPONENT_CLASS}__col`}>
                    <div className={`${COMPONENT_CLASS}__photo`}>
                        {user.profileImage ?
                            <img
                                src={user.profileImage}
                                alt="Profile" />
                            :
                            <ProfileIcon />
                        }
                    </div>
                </div>
                <div className={`${COMPONENT_CLASS}__col`}>
                    <div className={`${COMPONENT_CLASS}__fieldgroup`}>
                        <label>{t('manageAccount.biological')}</label>
                        <p>{user.biologicalSex === "Male" ? t('general.male') : t('general.female') || t('userProfile.header.notProvided')}</p>
                    </div>
                    <div className={`${COMPONENT_CLASS}__fieldgroup`}>
                        <label>{t('manageAccount.birthdate')}</label>
                        <p>{birthdate}</p>
                    </div>

                </div>
                <div className={`${COMPONENT_CLASS}__col`}>
                    {/* <div className={`${COMPONENT_CLASS}__fieldgroup`}>
                        <label>Wellness Program</label>
                        <p>{user.symmioAccess === SymmioAccessType.AppLicense ? "Yes" : "No"}</p>
                    </div> */}
                    <div className={`${COMPONENT_CLASS}__fieldgroup`}>
                        <label>{t('users.group')}</label>
                        <p>{user.groupName || t('users.noGroup')}</p>
                    </div>
                    { // if
                        user.roles && user.roles.length > 0 &&
                        <div className={`${COMPONENT_CLASS}__fieldgroup`}>
                            <label>{t('users.role')}</label>
                            <p>{user.roles.map(role => Utils.getRoleNameFromValue(role)).join(', ')}</p>
                        </div>
                    }
                    <div className={`${COMPONENT_CLASS}__fieldgroup`}>
                        <label>{t('general.email')}</label>
                        <p style={{ wordWrap: 'break-word' }}>{user.email}</p>
                    </div>
                </div>
                <div className={`${COMPONENT_CLASS}__col`}>
                    <div className={`${COMPONENT_CLASS}__fieldgroup`}>
                        <label>{t('users.tags')}</label>
                        <p className="flex flex-wrap gap-2 mt-4">
                            { // if
                                (user.tags != null && user.tags.length >= 1) &&
                                user.tags?.map((tag: UserTag, tagIndex: number) => (<TagComponent.Tag
                                    id={tag.id}
                                    key={`tag-${tagIndex}`}
                                    bgColor="#1216220D"
                                >{tag.name}</TagComponent.Tag>))
                            }
                            { // if
                                (user?.tags == null || user.tags.length === 0) &&
                                <p>--</p>
                            }
                        </p>
                    </div>
                    { // if
                        isManagingGroups(user.roles) &&
                        <div className={`${COMPONENT_CLASS}__fieldgroup`}>
                            <label>{t('users.managedGroups')}</label>
                            <p>
                                { // if
                                    user?.managedGroupIds != null && user.managedGroupIds.length >= 1 &&
                                    user.managedGroupIds.filter(r => groups.find(g => g.id === r) != null)
                                        .map((groupId: string, index: number) => (<TagComponent.Tag
                                            id={groupId}
                                            key={`manage-group-${index}`}
                                            bgColor="#1216220D"
                                        >{groups.find(g => g.id === groupId)!.name}</TagComponent.Tag>))
                                }
                                { // if
                                    (user?.managedGroupIds == null || user.managedGroupIds.length === 0) &&
                                    <p>--</p>
                                }
                            </p>
                        </div>
                    }
                    { // if
                        state?.organization?.id != null &&
                        user.organizationId != null &&
                        user.organizationId !== state.organization.id &&
                        <div className={`${COMPONENT_CLASS}__fieldgroup ${COMPONENT_CLASS}__highlighted`}>
                            <label style={{ backgroundColor: "rgb(255, 255, 204)" }}>{t('general.organization')}</label>
                            <p>{userOrgName}</p>
                        </div>
                    }
                </div>
            </div>
            <Modal
                isOpen={isEditing}
                isLoading={isSaving || isLoadingGroups || isLoadingTags || isLoadingOrganizations}
                onClose={handleCloseUserEditModal}
                defaultModalActions={true}
                submitButtonText={t('buttons.btn_saveChanges')}
                submitDisabled={!isBirthdateValid}
                onSubmit={() => handleSaveUserClick()}
                onCancel={handleCloseUserEditModal}>
                <ChangeAccountHolderModal
                    open={changeAccountHolderModalOpen}
                    onClose={handleAccountHolderModalClose}
                    currentAccountHolder={currentAccountHolder}
                    newAccountHolderId={editUser?.id}
                    organization={editUserOrg}
                />
                <RemoveAccountHolderModal
                    open={removeAccountHolderModalOpen}
                    onClose={handleAccountHolderModalClose}
                    accountHolder={user}
                    organization={editUserOrg}
                />

                <div className="flex flex-wrap items-center justify-between gap-4">
                    <h2 className="mt-2 mb-4">{t('userProfile.header.editProfile')}</h2>
                    {statusBadge}
                </div>

                {isUserLimitReached &&
                    <div className={`${COMPONENT_CLASS}__user-limit-reached`}>
                        {t('userProfile.header.userLimit')}
                    </div>
                }

                {canEditStatus &&
                    <>
                        <div className={`${COMPONENT_CLASS}__edit -full`}>
                            <ToggleButton
                                id={"toggleUserStatus"}
                                isChecked={isUserDeactivated}
                                onClick={handleToggleSwitch}
                                mainLabelText={t('userProfile.header.deactivate')}
                                subLabelText={t('userProfile.header.deactivationMessage')}
                            />
                        </div>

                        <hr style={{ borderColor: "rgba(18, 22, 34, 0.05)", margin: "1.5rem 0", maxWidth: "100%" }} />
                    </>
                }

                <h4 style={{ marginBottom: "1.5rem" }}>{t('userProfile.header.details')}</h4>
                <div className={`${COMPONENT_CLASS}__edit`}>
                    <div>
                        <label htmlFor="firstName">{t('manageAccount.firstName')}</label>
                        <TextTypeInput id="firstName"
                            type="text"
                            registerHook={register}
                            registerOptions={{
                                value: editUser?.firstName,
                                onChange: (e) => { setEditUser(Object.assign({}, editUser, { firstName: e.target.value })); }
                            }} />
                    </div>
                    <div>
                        <label htmlFor="lastName">{t('manageAccount.lastName')}</label>
                        <TextTypeInput
                            id="lastName"
                            type="text"
                            registerHook={register}
                            registerOptions={{
                                value: editUser?.lastName,
                                onChange: (e) => { setEditUser(Object.assign({}, editUser, { lastName: e.target.value })) }
                            }} />

                    </div>
                </div>

                <div className={`${COMPONENT_CLASS}__edit`}>
                    <div>
                        <label htmlFor="biologicalSex">{t('manageAccount.biological')}</label>
                        <Select
                            id="biologicalSex"
                            name="biologicalSex"
                            classNamePrefix={COMPONENT_CLASS}
                            placeholder={t('manageAccount.biological')}
                            options={BiologicalSexOptions}
                            onChange={(newValue: any, actionMeta: any) => setEditUser(Object.assign({}, editUser, { biologicalSex: newValue.value }))}
                            value={BiologicalSexOptions.find(o => o.value === editUser?.biologicalSex)}
                            styles={{
                                control: (baseStyles, state) => ({
                                    ...baseStyles,
                                    width: "100%",
                                }),
                            }}
                        />
                    </div>
                    <div>
                        <label htmlFor="birthdate">{t('manageAccount.birthdate')}</label>
                        <DateInputOption
                            answer={editUser?.birthdate ? dayjs(editUser.birthdate) : null}
                            onChange={handleBirthdateChange}
                            onClear={handleBirthdateClear}
                            onInvalidDate={() => setIsBirthdateValid(false)}
                        ></DateInputOption>

                        {!isBirthdateValid &&
                            <div className={`${COMPONENT_CLASS}__error-message`}>
                                {t('manageAccount.err_birtdateNotValid')}
                            </div>
                        }
                    </div>
                </div>

                <hr style={{ borderColor: "rgba(18, 22, 34, 0.05)", margin: "1.5rem 0", maxWidth: "100%" }} />

                <h4 style={{ marginBottom: "1.5rem" }}>{t('general.organization')}</h4>

                <div className={`${COMPONENT_CLASS}__edit ${!canChangeOrganization && "-full"}`}>
                    {
                        canChangeOrganization &&
                        <div>
                            <label htmlFor="organization">{t('general.organization')}</label>
                            {
                                (user.roles && user.roles[0] === UserRoles.ACCOUNT_HOLDER_ID) &&
                                <span className={`${COMPONENT_CLASS}__note`}>
                                    {t('userProfile.header.cantChange1')}
                                </span>
                            }
                            {
                                !(user.roles && user.roles[0] === UserRoles.ACCOUNT_HOLDER_ID)
                                && (editUser && editUser?.roles && editUser?.roles[0] === UserRoles.ACCOUNT_HOLDER_ID) &&
                                <span className={`${COMPONENT_CLASS}__note`}>
                                    {t('userProfile.header.cantChange2')}
                                </span>
                            }
                            <Select
                                id="organization"
                                name="organization"
                                classNamePrefix={COMPONENT_CLASS}
                                placeholder={t('general.organization')}
                                options={organizationOptions.map((o) => ({ label: o.name, value: o.id }))}
                                onChange={handleOrganizationChange}
                                value={editUser?.organizationId == null ? null : { label: organizationOptions.find(({ id }) => id === editUser?.organizationId)?.name, value: organizationOptions.find(({ id }) => id === editUser?.organizationId)?.id }}
                                isDisabled={
                                    (user.roles && user.roles[0] === UserRoles.ACCOUNT_HOLDER_ID)
                                    || (editUser && editUser?.roles && editUser?.roles[0] === UserRoles.ACCOUNT_HOLDER_ID)
                                }
                                styles={{
                                    control: (baseStyles, state) => ({
                                        ...baseStyles,
                                        width: "100%",
                                    }),
                                }}
                            />
                        </div>
                    }
                    <div>
                        <label htmlFor="role">{t('users.role')}</label>
                        {
                            noHolderOrgSwitch &&
                            <span className={`${COMPONENT_CLASS}__note`}>
                                {t('userProfile.header.note')}
                            </span>
                        }
                        { // if
                            canEditRoles() &&
                            <Select
                                isClearable={true}
                                id="role"
                                name="role"
                                classNamePrefix={COMPONENT_CLASS}
                                placeholder={t('users.role')}
                                options={roleDropdownOptions}
                                onChange={handleRoleChange}
                                value={role}
                                styles={{
                                    control: (baseStyles, state) => ({
                                        ...baseStyles,
                                        width: "100%",
                                    }),
                                }} />
                        }
                        { // if
                            !canEditRoles() &&
                            <p>{(user.roles ?? []).map(role => Utils.getRoleNameFromValue(role)).join(", ")}</p>
                        }
                        {
                            removingAccountHolder &&
                            <span className={`${COMPONENT_CLASS}__form-error`}>
                                {t('userProfile.header.removingAccHolder')}
                            </span>
                        }
                        {
                            (UserUtil.isAccountHolder(editUser)) &&
                            <div style={{ display: 'block', marginLeft: '1.5em' }}>
                                {
                                    overwritingAccountHolder &&
                                    <span className={`${COMPONENT_CLASS}__form-error`}>
                                        {t('userProfile.header.replacingAccHolder')}
                                    </span>
                                }
                            </div>
                        }
                    </div>
                </div>

                { // if
                    (UserUtil.isManager(editUser) || UserUtil.isTeamMember(editUser)) &&
                    <div className={`${COMPONENT_CLASS}__edit -full`}>
                        <div>
                            <label htmlFor="managerGroups">{t('users.managedGroups')}</label>
                            { // if
                                canEditRoles() &&
                                <Select
                                    isMulti
                                    isClearable={false}
                                    name="managerGroups"
                                    classNamePrefix={COMPONENT_CLASS}
                                    placeholder={t('users.managedGroups')}
                                    options={managedGroupOptions}
                                    onChange={(newValue: any, actionMeta: any) => setEditUser(Object.assign({}, editUser, { managedGroupIds: newValue.map((v: any) => v.value) }))}
                                    value={editUser?.managedGroupIds?.filter(r => r).map(r => managedGroupOptions.find(o => o.value === r))}
                                    styles={{
                                        control: (baseStyles, state) => ({
                                            ...baseStyles,
                                            width: "100%",
                                        }),
                                    }} />
                            }
                            { // if
                                !canEditRoles() &&
                                <p>
                                    {
                                        user?.managedGroupIds == null ||
                                            user.managedGroupIds.length === 0 ||
                                            groups == null ||
                                            groups.filter((g) => user.managedGroupIds!.indexOf(g.id) >= 0).length === 0
                                            ? "--"
                                            : groups.filter((g) => user.managedGroupIds!.indexOf(g.id) >= 0).map(g => g.name).join(", ")
                                    }
                                </p>
                            }
                        </div>
                    </div>
                }

                { // if
                    (editUser?.roles?.[0] === UserRoles.ADMIN_ID
                        && !OrganizationUtil.isSubOrganization(organization)
                        && (isUserAccountHolder || isSuperAdmin)
                        && subOrganizationOptions.length > 0) &&
                    <div className={`${COMPONENT_CLASS}__edit -full`}>
                        <div>
                            <label htmlFor="managedOrganizations">{t('users.subOrganizationAccess')}</label>
                            <Select
                                isMulti
                                isClearable={false}
                                name="managedOrganizations"
                                classNamePrefix={COMPONENT_CLASS}
                                placeholder={t('users.subOrganizationAccess')}
                                options={subOrganizationOptions
                                    .filter((o) => o.id != null)
                                    .map((o) => ({ label: o.name ?? '', value: o.id! }))}
                                onChange={(newValue: any, actionMeta: any) => setEditUser(Object.assign({}, editUser, { accessibleSubOrganizationIds: newValue.map((v: any) => v.value) }))}
                                value={(editUser?.accessibleSubOrganizationIds ?? [])
                                    .filter((id) => subOrganizationOptions.some((o) => o.id === id))
                                    .map((id) => ({ label: subOrganizationOptions.find((o) => o.id === id)?.name ?? '', value: id }))}
                                styles={{
                                    control: (baseStyles, state) => ({
                                        ...baseStyles,
                                        width: "100%",
                                    }),
                                }} />
                        </div>
                    </div>
                }

                <div className={`${COMPONENT_CLASS}__edit`}>
                    <div>
                        <label htmlFor="group">{t('users.group')}</label>
                        <Select
                            id="group"
                            name="group"
                            classNamePrefix={COMPONENT_CLASS}
                            placeholder={t('users.group')}
                            options={groups.map((g) => ({ label: g.name, value: g.id }))}
                            onChange={(newValue: any, actionMeta: any) => setEditUser(Object.assign({}, editUser, { groupName: newValue.label, groupId: newValue.value }))}
                            value={editUser?.groupId == null ? null : { label: editUser?.groupName, value: editUser?.groupId }}
                            styles={{
                                control: (baseStyles, state) => ({
                                    ...baseStyles,
                                    width: "100%",
                                }),
                            }} />
                    </div>
                    <div>
                        <label htmlFor="tag">{t('userProfile.header.tag')}</label>
                        <Select
                            isMulti
                            isClearable={true}
                            id="tag"
                            name="tag"
                            classNamePrefix={COMPONENT_CLASS}
                            placeholder={t('userProfile.header.tag')}
                            options={tags.map((t) => ({ label: t.name, value: t.id }))}
                            onChange={(newValue: any, actionMeta: any) => setEditUser(Object.assign({}, editUser, { tags: newValue.map((v: ListOptions) => ({ id: v.value, name: v.label })) }))}
                            value={editUser?.tags?.filter(t => t).map(t => ({ label: t.name, value: t.id }))}
                            styles={{
                                control: (baseStyles, state) => ({
                                    ...baseStyles,
                                    width: "100%",
                                }),
                            }} />
                    </div>
                </div>

                { // if
                    (UserUtil.isAdmin(state.user) || UserUtil.isManager(state.user) || UserUtil.isAccountHolder(state.user) || UserUtil.isSuperAdmin(state.claims)) && (
                        <div className={`${COMPONENT_CLASS}__edit -full ${overLicenseLimit ? `${COMPONENT_CLASS}__fieldgroup-error` : ''}`}>

                            {organization &&
                                <SymmioLicenseCheckbox
                                    organization={organization}
                                    onClick={handleSymmioAccessCheckbox}
                                    isChecked={isSymmioAccess}
                                    numberOfAddedLicenses={!hadSymmioAccessBefore && isSymmioAccess ? 1 : 0}
                                    numberOfRemovedLicenses={hadSymmioAccessBefore && !isSymmioAccess ? 1 : 0}
                                    isDisabled={editUser?.status === UserStatus.Disabled}
                                />
                            }
                        </div>
                    )
                }
                {
                    overLicenseLimit && (
                        <div className={`${COMPONENT_CLASS}__license-error`}>
                            {t('userProfile.header.limitReached')}
                        </div>
                    )
                }
            </Modal >
        </div >
    );
}

/**
 * TODO: FMS-1261 - This method can be entirely removed when this tech debt item is addressed.
 * Update the user count on any tags where the user may have been added or removed
 * @param user Current state of the user
 * @param editUser User with changes
 * @param currentUser (Optional) User that made the change
 */
async function updateTagUserCount(user: User, editUser: User, currentUser?: User | null) {
    let deletedTagIds: string[] = [];
    if (user.tagIds != null && user.tagIds.length > 0) {
        const newTagIds = editUser.tagIds || [];
        deletedTagIds = user.tagIds.filter(id => newTagIds.indexOf(id) === -1);
    }

    const isTagChange = deletedTagIds.length > 0 || (editUser.tagIds && editUser.tagIds.length > 0);
    if (isTagChange && editUser.tagIds != null && editUser.tagIds.length > 0) {
        for (const id of editUser.tagIds) {
            const tag = await TagService.get(id);

            if (tag) {
                tag.userCount = await UserService.getCountBy([{
                    field: "tagIds",
                    operator: "array-contains",
                    value: id,
                }]);
                await TagService.save(tag, currentUser);
            }
        }
    }

    for (const id of deletedTagIds) {
        const tag = await TagService.get(id);

        if (tag) {
            tag.userCount = await UserService.getCountBy([{
                field: "tagIds",
                operator: "array-contains",
                value: id,
            }]);
            await TagService.save(tag, currentUser);
        }
    }
}

export default UserProfileHeader;

